import React, { useState } from 'react';
import { Stack, Button, Chip, IconButton, Menu, MenuItem } from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { useMutation, useQuery } from '@apollo/client/react';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import DeleteIcon from '@mui/icons-material/Delete';
import {
  GetRecurringExpensesForUnitDocument,
  GetRecurringExpensesForUnitQuery,
  GetRecurringExpensesForUnitQueryVariables,
  CreateRecurringExpenseDocument,
  CreateRecurringExpenseMutation,
  CreateRecurringExpenseMutationVariables,
  UpdateRecurringExpenseDocument,
  UpdateRecurringExpenseMutation,
  UpdateRecurringExpenseMutationVariables,
  SetRecurringExpenseUnitAssignmentsDocument,
  SetRecurringExpenseUnitAssignmentsMutation,
  SetRecurringExpenseUnitAssignmentsMutationVariables,
  DeleteRecurringExpenseDocument,
  DeleteRecurringExpenseMutation,
  DeleteRecurringExpenseMutationVariables,
  SuspendRecurringExpenseDocument,
  SuspendRecurringExpenseMutation,
  SuspendRecurringExpenseMutationVariables,
  UnsuspendRecurringExpenseDocument,
  UnsuspendRecurringExpenseMutation,
  UnsuspendRecurringExpenseMutationVariables,
  RecurringExpenseFragmentFragment,
  RecurringExpenseFrequency,
} from '../../../generated/graphql';
import { useAlert } from '@dwelvio/react-alert';
import { AddIcon } from '../../../components/icons';
import {
  NewRecurringExpenseDialog,
  RecurringExpenseSubmitData,
} from '../../../components/new-recurring-expense-dialog';
import { useApp } from '../../../components/app-context';

interface UnitRecurringExpensesSectionProps {
  unitId: string;
  disabled?: boolean;
}

const frequencyLabels: Record<RecurringExpenseFrequency, string> = {
  W: 'Týždenne',
  W2: 'Každé 2 týždne',
  M: 'Mesačne',
  M2: 'Každé 2 mesiace',
  M3: 'Každé 3 mesiace',
  M6: 'Polročne',
  Y: 'Ročne',
};

export function UnitRecurringExpensesSection({ unitId, disabled }: UnitRecurringExpensesSectionProps) {
  const app = useApp();
  const { show } = useAlert();
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [editingExpense, setEditingExpense] = useState<RecurringExpenseFragmentFragment | null>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedExpense, setSelectedExpense] = useState<RecurringExpenseFragmentFragment | null>(null);

  const { data, refetch, error } = useQuery<
    GetRecurringExpensesForUnitQuery,
    GetRecurringExpensesForUnitQueryVariables
  >(GetRecurringExpensesForUnitDocument, {
    variables: { unitId },
  });

  const [createRecurringExpense, { loading: createLoading }] = useMutation<
    CreateRecurringExpenseMutation,
    CreateRecurringExpenseMutationVariables
  >(CreateRecurringExpenseDocument, {
    onError: (e: Error) => show.error('Nepodarilo sa vytvoriť pravidelný výdavok', e.message),
  });

  const [updateRecurringExpense, { loading: updateLoading }] = useMutation<
    UpdateRecurringExpenseMutation,
    UpdateRecurringExpenseMutationVariables
  >(UpdateRecurringExpenseDocument, {
    onError: (e: Error) => show.error('Nepodarilo sa upraviť pravidelný výdavok', e.message),
  });

  const [setUnitAssignments] = useMutation<
    SetRecurringExpenseUnitAssignmentsMutation,
    SetRecurringExpenseUnitAssignmentsMutationVariables
  >(SetRecurringExpenseUnitAssignmentsDocument, {
    onError: (e: Error) =>
      show.error('Pravidelný výdavok bol vytvorený, ale nepodarilo sa priradiť k nehnuteľnosti.', e.message),
  });

  const [deleteRecurringExpense] = useMutation<DeleteRecurringExpenseMutation, DeleteRecurringExpenseMutationVariables>(
    DeleteRecurringExpenseDocument,
    {
      onCompleted: () => {
        show.info('Pravidelný výdavok bol odstránený');
        refetch();
      },
      onError: (e: Error) => show.error('Nepodarilo sa odstrániť pravidelný výdavok', e.message),
    },
  );

  const [suspendRecurringExpense] = useMutation<
    SuspendRecurringExpenseMutation,
    SuspendRecurringExpenseMutationVariables
  >(SuspendRecurringExpenseDocument, {
    onError: (e: Error) => show.error('Nepodarilo sa pozastaviť pravidelný výdavok', e.message),
  });

  const [unsuspendRecurringExpense] = useMutation<
    UnsuspendRecurringExpenseMutation,
    UnsuspendRecurringExpenseMutationVariables
  >(UnsuspendRecurringExpenseDocument, {
    onError: (e: Error) => show.error('Nepodarilo sa obnoviť pravidelný výdavok', e.message),
  });

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, expense: RecurringExpenseFragmentFragment) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setSelectedExpense(expense);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedExpense(null);
  };

  const handleSuspend = () => {
    if (selectedExpense) {
      suspendRecurringExpense({ variables: { recurringExpenseId: selectedExpense.id } });
    }
    handleMenuClose();
  };

  const handleUnsuspend = () => {
    if (selectedExpense) {
      unsuspendRecurringExpense({ variables: { recurringExpenseId: selectedExpense.id } });
    }
    handleMenuClose();
  };

  const handleDelete = () => {
    if (selectedExpense) {
      deleteRecurringExpense({ variables: { recurringExpenseId: selectedExpense.id } });
    }
    handleMenuClose();
  };

  const handleAddSubmit = async (input: RecurringExpenseSubmitData) => {
    const result = await createRecurringExpense({
      variables: {
        input: {
          title: input.title,
          amount: input.amount,
          taxDeductible: input.taxDeductible,
          refNo: input.refNo,
          frequency: input.frequency,
          nextDate: input.nextDate,
          stopAfterDate: input.stopAfterDate,
          dueDays: input.dueDays,
        },
      },
    });

    const createdId = result.data?.createRecurringExpense?.recurringExpense?.id;
    if (!createdId) return;

    if (input.unitAssignments.length > 0) {
      await setUnitAssignments({
        variables: {
          recurringExpenseId: createdId,
          assignments: input.unitAssignments,
        },
      });
    }

    setShowAddDialog(false);
    refetch();
  };

  const handleEditSubmit = async (input: RecurringExpenseSubmitData) => {
    if (!editingExpense) return;

    await updateRecurringExpense({
      variables: {
        input: {
          id: editingExpense.id,
          title: input.title,
          amount: input.amount,
          taxDeductible: input.taxDeductible,
          refNo: input.refNo,
          frequency: input.frequency,
          nextDate: input.nextDate,
          stopAfterDate: input.stopAfterDate,
          dueDays: input.dueDays,
        },
      },
    });

    await setUnitAssignments({
      variables: {
        recurringExpenseId: editingExpense.id,
        assignments: input.unitAssignments,
      },
    });

    setEditingExpense(null);
    refetch();
  };

  const columns = React.useMemo((): GridColDef<RecurringExpenseFragmentFragment>[] => {
    return [
      {
        field: 'title',
        headerName: 'Názov',
        flex: 1,
        minWidth: 150,
      },
      {
        field: 'amount',
        headerName: 'Suma',
        width: 120,
        valueFormatter: (value: number) => app.formatNumber(value, 2) + ' €',
      },
      {
        field: 'frequency',
        headerName: 'Frekvencia',
        width: 150,
        valueFormatter: (value: RecurringExpenseFrequency) => frequencyLabels[value] || value,
      },
      {
        field: 'nextDate',
        headerName: 'Nasledujúci',
        width: 130,
        valueFormatter: (value: string) => app.formatDate(value),
      },
      {
        field: 'suspended',
        headerName: 'Stav',
        width: 120,
        renderCell: (params) => {
          if (params.row.deletedAt) {
            return <Chip label="Odstránený" color="error" size="small" />;
          }
          return params.value ? (
            <Chip label="Pozastavený" color="warning" size="small" />
          ) : (
            <Chip label="Aktívny" color="success" size="small" />
          );
        },
      },
      {
        field: 'actions',
        headerName: '',
        width: 60,
        sortable: false,
        renderCell: (params) => (
          <IconButton size="small" onClick={(e) => handleMenuOpen(e, params.row)} disabled={disabled}>
            <MoreVertIcon />
          </IconButton>
        ),
      },
    ];
  }, [app, disabled]);

  if (error) {
    return <div>Nepodarilo sa načítať pravidelné výdavky: {error.message}</div>;
  }

  const anyLoading = createLoading || updateLoading;

  return (
    <Stack spacing={2}>
      <Stack direction="row" justifyContent="flex-start">
        <Button variant="outlined" startIcon={<AddIcon />} onClick={() => setShowAddDialog(true)} disabled={disabled}>
          Pridať
        </Button>
      </Stack>

      <DataGrid
        rows={data?.getRecurringExpensesForUnit || []}
        columns={columns}
        initialState={{
          pagination: { paginationModel: { pageSize: 10 } },
          sorting: { sortModel: [{ field: 'nextDate', sort: 'asc' }] },
        }}
        pageSizeOptions={[10, 20, 50]}
        disableRowSelectionOnClick
        onRowClick={(params) => {
          if (!disabled) setEditingExpense(params.row);
        }}
        autoHeight
        sx={{ '& .MuiDataGrid-row': { cursor: !disabled ? 'pointer' : 'default' } }}
      />

      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
        {selectedExpense && !selectedExpense.deletedAt && (
          <>
            {selectedExpense.suspended ? (
              <MenuItem onClick={handleUnsuspend}>
                <PlayArrowIcon fontSize="small" sx={{ mr: 1 }} />
                Aktivovať
              </MenuItem>
            ) : (
              <MenuItem onClick={handleSuspend}>
                <PauseIcon fontSize="small" sx={{ mr: 1 }} />
                Pozastaviť
              </MenuItem>
            )}
            <MenuItem onClick={handleDelete}>
              <DeleteIcon fontSize="small" sx={{ mr: 1 }} />
              Odstrániť
            </MenuItem>
          </>
        )}
      </Menu>

      {showAddDialog && (
        <NewRecurringExpenseDialog
          open={showAddDialog}
          onClose={() => setShowAddDialog(false)}
          onSubmit={handleAddSubmit}
          loading={anyLoading}
          initialUnitId={unitId}
        />
      )}

      {editingExpense && (
        <NewRecurringExpenseDialog
          open={true}
          editExpense={editingExpense}
          onClose={() => setEditingExpense(null)}
          onSubmit={handleEditSubmit}
          loading={anyLoading}
        />
      )}
    </Stack>
  );
}
